/**
 * Article YouTube Video Controller
 * Detects links with timestamp (t parameter) in article content and makes the YouTube video jump to that time
 */

class ArticleYouTubeController {
  constructor() {
    this.videoContainer = document.querySelector('#article-media');
    this.iframe = this.videoContainer ? this.videoContainer.querySelector('iframe') : null;
    this.player = null;
    this.articleContent = document.querySelector('.rr-article-content');
    
    if (!this.iframe || !this.articleContent) return;
    
    this.init();
  }
  
  init() {
    // Load YouTube IFrame API if not already loaded
    if (!window.YT) {
      this.loadYouTubeAPI();
    } else if (window.YT.Player) {
      this.initializePlayer();
    } else {
      // API loaded but not ready yet
      window.onYouTubeIframeAPIReady = () => this.initializePlayer();
    }
    
    // Set up link handlers
    this.setupLinkHandlers();
  }
  
  loadYouTubeAPI() {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    
    // Set up callback for when API is ready
    window.onYouTubeIframeAPIReady = () => this.initializePlayer();
  }
  
  initializePlayer() {
    // Get the iframe's src to extract video ID
    const iframeSrc = this.iframe.src;
    const videoId = this.extractVideoId(iframeSrc);
    
    if (!videoId) {
      console.warn('Could not extract YouTube video ID from iframe');
      return;
    }
    
    // Replace iframe with a div that YouTube API can use
    const playerDiv = document.createElement('div');
    playerDiv.id = 'article-youtube-player';
    this.iframe.parentNode.replaceChild(playerDiv, this.iframe);
    
    // Check if there's a timestamp in the URL
    const urlTimestamp = this.getUrlTimestamp();
    
    // Initialize YouTube player
    this.player = new YT.Player('article-youtube-player', {
      videoId: videoId,
      playerVars: {
        rel: 0,
        modestbranding: 1,
        // If there's a timestamp in URL, start at that time
        start: urlTimestamp || undefined
      },
      events: {
        onReady: (event) => {
          console.log('YouTube player ready');
          
          // If there's a timestamp, seek to it and play
          if (urlTimestamp) {
            event.target.seekTo(urlTimestamp, true);
            event.target.playVideo();
            
            // Scroll to video
            this.scrollToVideo();
          }
        }
      }
    });
  }
  
  getUrlTimestamp() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const tParam = urlParams.get('t');
      
      if (!tParam) return null;
      
      return this.parseTimestamp(tParam);
    } catch (e) {
      return null;
    }
  }
  
  extractVideoId(url) {
    // Extract video ID from various YouTube URL formats
    const patterns = [
      /youtube\.com\/embed\/([^?&]+)/,
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?&]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  }
  
  setupLinkHandlers() {
    // Find all links in the article content
    const links = this.articleContent.querySelectorAll('a[href]');
    
    links.forEach(link => {
      const href = link.getAttribute('href');
      const timestamp = this.extractTimestamp(href);
      
      if (timestamp !== null) {
        // This link has a timestamp parameter
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.jumpToTimestamp(timestamp);
          
          // Update the URL without reloading the page
          this.updateUrlTimestamp(timestamp);
        });
        
        // Add visual indicator (optional)
        link.classList.add('video-timestamp-link');
      }
    });
  }
  
  updateUrlTimestamp(seconds) {
    // Update the browser's URL with the timestamp parameter
    const url = new URL(window.location);
    url.searchParams.set('t', seconds);
    window.history.pushState({}, '', url);
  }
  
  extractTimestamp(url) {
    try {
      // Handle both full URLs and relative links
      const urlObj = new URL(url, window.location.origin);
      const tParam = urlObj.searchParams.get('t');
      
      if (!tParam) return null;
      
      // Parse timestamp - can be in seconds (123) or time format (1m23s, 1h2m3s)
      const parsedSeconds = this.parseTimestamp(tParam);
      
      console.log('[YouTube Controller] Timestamp Click:', {
        url,
        tParam,
        parsedSeconds,
        formatted: `${Math.floor(parsedSeconds / 60)}:${(parsedSeconds % 60).toString().padStart(2, '0')}`
      });
      
      return parsedSeconds;
    } catch (e) {
      console.error('[YouTube Controller] Error extracting timestamp:', e);
      return null;
    }
  }
  
  parseTimestamp(timestamp) {
    // Handle multiple formats: "123" (seconds), "3m22s", "1h2m3s"
    
    // First try simple integer (most common: t=123)
    const simpleSeconds = parseInt(timestamp, 10);
    if (!isNaN(simpleSeconds) && timestamp === simpleSeconds.toString()) {
      return simpleSeconds > 0 ? simpleSeconds : null;
    }
    
    // Parse time format: 1h2m3s, 3m22s, 45s
    let totalSeconds = 0;
    
    // Extract hours (e.g., "1h")
    const hoursMatch = timestamp.match(/(\d+)h/);
    if (hoursMatch) {
      totalSeconds += parseInt(hoursMatch[1], 10) * 3600;
    }
    
    // Extract minutes (e.g., "3m")
    const minutesMatch = timestamp.match(/(\d+)m/);
    if (minutesMatch) {
      totalSeconds += parseInt(minutesMatch[1], 10) * 60;
    }
    
    // Extract seconds (e.g., "22s")
    const secondsMatch = timestamp.match(/(\d+)s/);
    if (secondsMatch) {
      totalSeconds += parseInt(secondsMatch[1], 10);
    }
    
    return totalSeconds > 0 ? totalSeconds : null;
  }
  
  jumpToTimestamp(seconds) {
    if (!this.player || !this.player.seekTo) {
      console.warn('[YouTube Controller] YouTube player not ready');
      return;
    }
    
    const playerState = this.player.getPlayerState ? this.player.getPlayerState() : null;
    const willPlay = playerState !== YT.PlayerState.PLAYING;
    
    // Seek to the timestamp
    this.player.seekTo(seconds, true);
    
    // Play the video if it's not playing
    if (willPlay) {
      this.player.playVideo();
    }
    
    console.log('[YouTube Controller] Video Control:', {
      seekedTo: seconds,
      formatted: `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`,
      wasPlaying: !willPlay,
      startedPlayback: willPlay
    });
    
    // Smooth scroll back to the video
    this.scrollToVideo();
  }
  
  scrollToVideo() {
    if (!this.videoContainer) return;
    
    // Use ScrollUtils for consistent header-aware scrolling
    if (window.ScrollUtils) {
      window.ScrollUtils.scrollToElement(this.videoContainer, {
        extraOffset: 20,
        behavior: 'smooth'
      });
    } else {
      // Fallback if ScrollUtils not loaded
      console.warn('ScrollUtils not loaded, using basic scroll');
      this.videoContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ArticleYouTubeController();
  });
} else {
  new ArticleYouTubeController();
}
